/* eslint-disable max-lines-per-function */
import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';
import { matchMergeOption, createDefaultWatermark } from '../../../helper/standardChart';

const visInfo: Layer['visInfo'] = {
  id: 'dynamicBubble',
  name_en: 'Dynamic Bubble',
  name_zh: '动态气泡图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    value: valuesArray => valuesArray.length >= 3,
    name: amount => amount > 1,
    data: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    name: { itemType: [DEFINE.STR], category: DEFINE.SINGLE },
    value: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.ARRAY
    },
    group: { itemType: [DEFINE.DATE], category: DEFINE.SINGLE }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    name: string;
    value: string;
    group: string;
  };
  animationDuration: string;
  dvShowWaterMark?: boolean;
}

/** 暂不支持legend的点击功能 */
export const dynamicBubble = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  withTimeline: true,
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util, hook, layout } = api;
    const chartViewRect = $dom.getBoundingClientRect();
    const presetOption = hook.buildStandardChartPresetOption(api);
    const {
      dataIndex = 0,
      encoding: { name, value: valuesArray, group: groupBy },
      animationDuration: updateFrequency = 1200,
      dvShowWaterMark = false
    } = spec;
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);

    // * ------------------------- data
    const sizeRangeMap = {
      [util.const.LAYOUT.SMALL]: [8, 28],
      [util.const.LAYOUT.SMALL_2_1]: [8, 46],
      [util.const.LAYOUT.SMALL_1_2]: [8, 46],
      [util.const.LAYOUT.LARGE]: [16, 92],
      [util.const.LAYOUT.LARGE_3_2]: [20, 120]
    };
    const range = sizeRangeMap[layout];
    const [x, y, size] = valuesArray;
    // 解析数据-获取年份
    const colorList = presetOption?.color || [
      '#FF9500',
      '#14CCBD',
      '#1A9FFF',
      '#3366FF',
      '#4433FF',
      '#FF33AA',
      '#CC41D9',
      '#FFAD29',
      '#38D9C6',
      '#42B7FF',
      '#5C8AFF',
      '#6F5CFF',
      '#FF5CB6',
      '#DF6AE6',
      '#FFC252',
      '#60E6D2',
      '#6BCBFF',
      '#85ABFF',
      '#9785FF',
      '#FF85C4',
      '#F196F2',
      '#FFD57A',
      '#8DF2E0',
      '#94DDFF',
      '#ADCAFF',
      '#BCADFF',
      '#FFADD5',
      '#FFC7FE'
    ];
    const transformData = util.dataTransform.transform(data.values.slice(), [
      { type: 'sort', field: groupBy, order: 'ascending' }
    ]);
    const { nameList, dates } = util.dataEncoding.encoding(transformData, {
      nameList: { field: name, transforms: [{ type: 'deDuplication' }] },
      dates: {
        field: groupBy,
        transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
      }
    });
    const nameColorMap = {};
    nameList.forEach((item, index) => {
      nameColorMap[item] = colorList[index % colorList.length];
    });

    // 解析数据-数据分组
    const dataArr = dates.map(date => {
      return util.dataTransform.transform(transformData, [
        { type: 'filter', cb: item => item[groupBy] === date }
      ]);
    });

    // 申明配置项数组
    const options = dataArr.map((dataItem, index) => {
      // 格式化数据
      const formatData = dataItem.map((item, index) => {
        return {
          id: item[name],
          name: item[name],
          value: [+item[x], +item[y], typeof size === 'undefined' ? 1 : +item[size]],
          itemStyle: [
            {
              id: 'circle',
              // 用于legend取颜色
              color: nameColorMap[item[name]],
              style: {
                fill: nameColorMap[item[name]]
              }
            }
          ]
        };
      });
      const graphic = createDefaultWatermark(chartViewRect, dates[index]);

      // 生成每一周期的配置项
      return {
        animationDuration: updateFrequency,
        animationDurationUpdate: updateFrequency,
        dvWaterMark: { show: false },
        grid: [
          {
            top: 0
          }
        ],
        axisPointer: {
          label: {
            // 气泡图（dvScatter）独有字段，控制axisPointer的x轴标签显示在线的上方还是下方（为true时显示在线的上方）
            dvEnableXOffset: false
          }
        },
        tooltip: {
          ...presetOption?.tooltip,
          formatter: params_ => {
            const { name, value } = params_;
            const resultX = hook.formatDataNumberValue(value[0], {
              type: 'table',
              unit: dataMetaInfo[x]?.unit
            });
            const resultY = hook.formatDataNumberValue(value[1], {
              type: 'table',
              unit: dataMetaInfo[y]?.unit
            });
            const resultSize = hook.formatDataNumberValue(value[2], {
              type: 'table',
              unit: dataMetaInfo[size]?.unit
            });
            const items = [
              {
                name: x,
                value: `${resultX.value + resultX.unit}`
              },
              {
                name: y,
                value: `${resultY.value + resultY.unit}`
              },
              {
                name: size,
                value: `${resultSize.value + resultSize.unit}`
              }
            ];

            return hook.buildTooltip({
              config: hook.buildStandardChartPresetOption(api).tooltip,
              title: name || '',
              items
            });
          }
        },
        xAxis: [
          {
            show: true,
            name: x + (dataMetaInfo[x]?.unit ? `(${dataMetaInfo[x].unit})` : ''),
            type: 'value',
            axisPointer: {
              label: {
                formatter: params => {
                  const result = hook.formatDataNumberValue(params.value);
                  return result.value + result.unit;
                }
              }
            }
          }
        ],
        yAxis: [
          {
            show: true,
            name: y + (dataMetaInfo[y]?.unit ? `(${dataMetaInfo[y].unit})` : ''),
            dvNameLocation: 'left',
            type: 'value',
            axisPointer: {
              label: {
                formatter: params => {
                  const result = hook.formatDataNumberValue(params.value);
                  return result.value + result.unit;
                }
              }
            }
          }
        ],
        graphic: dvShowWaterMark ? graphic : undefined,
        series: [
          {
            name,
            type: 'dvScatter',
            data: formatData,
            label: {
              show: true,
              formatter: '{b}'
            },
            dvSymbolSize: range
          }
        ]
      };
    });

    return renderApi => {
      const renderParams = {
        option: options[options.length - 1]
      };
      renderParams.option = renderApi.mergeChartOptionProxy(renderParams.option);

      const timelineIns = renderApi.renderTimelineProxy(dates as string[]);
      const legendIns = renderApi.renderLegendProxy(renderParams.option.legend.data);
      const chartIns = renderApi.renderChartProxy(renderParams, { theme: 'mobile-app-light' });

      const helperOptions = options.map(option => {
        const mergeOption = matchMergeOption(option, renderApi.mergeChartOptionProxy(option), {
          override: true
        });
        renderApi.optionCallbackProxy(mergeOption);
        return mergeOption;
      });

      const helper = new ThsDataVStandardChart.helper.DynamicDvScatterHelper(
        helperOptions,
        chartIns,
        timelineIns,
        {
          axisRangeMerge: 'custom'
        }
      );
      timelineIns?.on('change', e => {
        legendIns?.update(helperOptions[e.index].legend.data);
      });
    };
  }
});
